import React, { useMemo } from 'react'
import { useQuery } from '@apollo/client'
import { GET_FABRIC_PRICE_CATEGORIES_FOR_PALETTE } from '../fabricPaletteQueries'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { Badge } from '@/components/ui/badge'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'

export interface PaletteFabric {
  id: number
  code: string
  color_name?: string | null
  order?: number | null
  image?: { src?: string | null; src_thumbnail?: string | null } | null
}

export type OverrideSource = 'pim' | 'admin'

export interface ColorOverride {
  name: string
  category: string
  /** source as loaded from the backend (legacy entries without one = pim) */
  name_source: OverrideSource
  category_source: OverrideSource
  /** values as loaded, to detect user edits */
  loaded_name: string
  loaded_category: string
}

const emptyOverride: ColorOverride = {
  name: '',
  category: '',
  name_source: 'pim',
  category_source: 'pim',
  loaded_name: '',
  loaded_category: '',
}

const SourceMarker = ({
  value,
  loaded,
  source,
}: {
  value: string
  loaded: string
  source: OverrideSource
}) => {
  if (!value.trim()) return null
  const edited = value !== loaded || source === 'admin'
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span>
          <Badge variant={edited ? 'default' : 'secondary'}>
            {edited ? 'edited' : 'PIM'}
          </Badge>
        </span>
      </TooltipTrigger>
      <TooltipContent>
        PIM values are refreshed by the nightly import; edited values are kept.
      </TooltipContent>
    </Tooltip>
  )
}

export interface GroupColorState {
  /** true = every colour of the group (color_codes null) */
  all: boolean
  /** selected Fabric.code values (only meaningful when all is false) */
  selected: string[]
  /** per-code overrides, kept even when the colour is not selected */
  overrides: Record<string, ColorOverride>
  dirty: boolean
}

// Radix Select forbids an empty-string item value
const DEFAULT_VALUE = '__default__'

interface Props {
  fabrics: PaletteFabric[]
  /** manufacturer of the fabric group, source of the price categories */
  manufacturerId?: number | null
  /** the group's own price category numbers (as strings) */
  defaultCategories?: string[]
  state: GroupColorState
  onChange: (next: GroupColorState) => void
}

export const sortFabrics = (fabrics: PaletteFabric[]) =>
  [...fabrics].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))

/** x / y label for the accordion header badge */
export const countLabel = (fabrics: PaletteFabric[], state: GroupColorState) => {
  const total = fabrics.length
  if (state.all) return `${total} / ${total} colours`
  const codes = new Set(fabrics.map(f => f.code))
  const n = state.selected.filter(c => codes.has(c)).length
  return `${n} / ${total} colours`
}

export function PaletteGroupColors({
  fabrics,
  manufacturerId,
  defaultCategories = [],
  state,
  onChange,
}: Props) {
  // one request per manufacturer (Apollo caches / dedupes by variables)
  const { data: catData } = useQuery(GET_FABRIC_PRICE_CATEGORIES_FOR_PALETTE, {
    variables: { manufacturerId },
    skip: manufacturerId == null,
  })
  const categoryNumbers: string[] = useMemo(
    () =>
      (
        (catData?.findManyFabricPriceCategory ?? []) as {
          group_number: number
        }[]
      ).map(c => String(c.group_number)),
    [catData]
  )
  const defaultLabel = defaultCategories.length
    ? `Default (${defaultCategories.map(c => `CAT ${c}`).join(', ')})`
    : 'Default'

  const sorted = useMemo(() => sortFabrics(fabrics), [fabrics])
  const known = useMemo(() => new Set(fabrics.map(f => f.code)), [fabrics])
  const missing = state.selected.filter(c => !known.has(c))

  const update = (patch: Partial<GroupColorState>) =>
    onChange({ ...state, ...patch, dirty: true })

  const toggleAll = (checked: boolean) => {
    if (checked) {
      update({ all: true })
    } else {
      // switching off starts from "everything checked" so nothing is lost silently
      update({
        all: false,
        selected: state.selected.length
          ? state.selected
          : sorted.map(f => f.code),
      })
    }
  }

  const toggleCode = (code: string, checked: boolean) => {
    const set = new Set(state.selected)
    if (checked) set.add(code)
    else set.delete(code)
    update({ selected: Array.from(set) })
  }

  const setOverride = (code: string, patch: Partial<ColorOverride>) => {
    const cur = state.overrides[code] ?? emptyOverride
    update({ overrides: { ...state.overrides, [code]: { ...cur, ...patch } } })
  }

  const renderRow = (
    code: string,
    fabric: PaletteFabric | undefined
  ) => {
    const checked = state.all || state.selected.includes(code)
    const ov = state.overrides[code] ?? emptyOverride
    return (
      <div
        key={code}
        className="grid grid-cols-[24px_40px_80px_1fr_1fr_1fr] gap-2 items-center py-1 border-b"
      >
        <Checkbox
          checked={checked}
          disabled={state.all}
          onCheckedChange={v => toggleCode(code, v === true)}
        />
        {fabric?.image?.src_thumbnail || fabric?.image?.src ? (
          <img
            src={(fabric.image.src_thumbnail || fabric.image.src) as string}
            alt={code}
            className="h-8 w-8 rounded object-cover"
          />
        ) : (
          <div className="h-8 w-8 rounded bg-muted" />
        )}
        <span className="text-sm font-mono">{code}</span>
        {fabric ? (
          <span className="text-sm truncate">{fabric.color_name}</span>
        ) : (
          <Badge variant="destructive">missing colour</Badge>
        )}
        <div className="flex items-center gap-1">
          <Input
            value={ov.name}
            placeholder={fabric?.color_name || 'Custom name'}
            onChange={e => setOverride(code, { name: e.target.value })}
          />
          <SourceMarker
            value={ov.name}
            loaded={ov.loaded_name}
            source={ov.name_source}
          />
        </div>
        <div className="flex items-center gap-1">
          <Select
            value={
              ov.category.trim() && !defaultCategories.includes(ov.category)
                ? ov.category
                : DEFAULT_VALUE
            }
            onValueChange={v =>
              setOverride(code, {
                category: v === DEFAULT_VALUE ? '' : v,
              })
            }
          >
            <SelectTrigger>
              <SelectValue placeholder={defaultLabel} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={DEFAULT_VALUE}>{defaultLabel}</SelectItem>
              {categoryNumbers
                .filter(n => !defaultCategories.includes(n))
                .map(n => (
                  <SelectItem key={n} value={n}>
                    CAT {n}
                  </SelectItem>
                ))}
              {ov.category.trim() &&
                !categoryNumbers.includes(ov.category) &&
                !defaultCategories.includes(ov.category) && (
                <SelectItem value={ov.category}>
                  CAT {ov.category} (unknown)
                </SelectItem>
              )}
            </SelectContent>
          </Select>
          <SourceMarker
            value={ov.category}
            loaded={ov.loaded_category}
            source={ov.category_source}
          />
        </div>
      </div>
    )
  }

  return (
    <TooltipProvider>
    <div className="space-y-3">
      <label className="flex items-center gap-2 text-sm">
        <Switch checked={state.all} onCheckedChange={toggleAll} />
        All colours
      </label>
      {!state.all && state.selected.length === 0 && (
        <p className="text-sm text-destructive">
          Select at least one colour, or switch on "All colours".
        </p>
      )}
      <div>
        {sorted.map(f => renderRow(f.code, f))}
        {missing.map(c => renderRow(c, undefined))}
      </div>
    </div>
    </TooltipProvider>
  )
}
